import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { X, Link2, Copy, Trash2, Clock, Eye, FileText, CheckCircle2, Loader2, Download } from "lucide-react";
import { api } from "../lib/api";

/**
 * Modale admin per generare e gestire i link temporanei 48h che condividono
 * asset fornitori con clienti esterni. Il cliente dovrà accettare il disclaimer
 * (nome + email) prima di scaricare — accettazione firmata nel registro audit.
 */
function fmtSize(bytes) {
  const n = Number(bytes) || 0;
  if (n <= 0) return "";
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function hoursLeft(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60)));
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Link copiato negli appunti");
  } catch {
    window.prompt("Copia il link:", text);
  }
}

export default function AssetShareLinkModal({ open, onClose, selectedAssets = [], supplierName = "" }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [created, setCreated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/asset-share-links");
      setLinks(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento link");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setCreated(null);
    setName(supplierName ? `${supplierName} · ${new Date().toLocaleDateString("it-IT")}` : "");
    load();
  }, [open, supplierName, load]);

  async function createLink() {
    if (creating || selectedAssets.length === 0) return;
    setCreating(true);
    try {
      const { data } = await api.post("/admin/asset-share-links", {
        asset_ids: selectedAssets.map((a) => a.id),
        name: name.trim(),
      });
      setCreated(data);
      toast.success("Link 48h generato");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore generazione link");
    } finally {
      setCreating(false);
    }
  }

  async function deleteLink(sid) {
    if (!window.confirm("Revocare questo link? Il cliente non potrà più accedere ai file.")) return;
    try {
      await api.delete(`/admin/asset-share-links/${sid}`);
      toast.success("Link revocato");
      if (created?.id === sid) setCreated(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore revoca");
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9500] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4"
      data-testid="asset-share-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-cream w-[min(96vw,720px)] max-h-[90vh] overflow-hidden flex flex-col border border-ink/15 shadow-2xl rounded-sm" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-ink/10 flex items-start justify-between gap-3 bg-white">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 shrink-0 rounded-sm bg-gold/15 text-gold flex items-center justify-center">
              <Link2 size={18} />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Condivisione temporanea</div>
              <div className="text-base font-light text-ink mt-0.5">Link 48 ore per clienti esterni</div>
            </div>
          </div>
          <button onClick={() => onClose?.()} className="text-slate1 hover:text-ink p-1 rounded-sm hover:bg-ink/5 transition-colors" aria-label="Chiudi" data-testid="asset-share-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-6">
          {/* Sezione creazione */}
          {selectedAssets.length > 0 && (
            <div className="bg-white border border-ink/10 rounded-sm p-4 space-y-3" data-testid="asset-share-create-section">
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">
                Nuovo link · {selectedAssets.length} file selezionat{selectedAssets.length === 1 ? "o" : "i"}
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {selectedAssets.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 text-xs text-ink/80">
                    <FileText size={11} className="text-slate1 shrink-0" />
                    <span className="truncate">{a.title || a.filename}</span>
                    {a.size > 0 && <span className="text-slate1 shrink-0">{fmtSize(a.size)}</span>}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nome del link (es. Cliente Rossi · Listini)"
                  className="flex-1 min-w-[220px] px-3 py-2 text-sm bg-cream border border-ink/15 rounded-sm focus:outline-none focus:border-ink"
                  data-testid="asset-share-name-input"
                />
                <button
                  onClick={createLink}
                  disabled={creating}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/90 disabled:opacity-60 transition-colors"
                  data-testid="asset-share-generate-btn"
                >
                  {creating ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />}
                  {creating ? "Generazione…" : "Genera link 48h"}
                </button>
              </div>
              <p className="text-[11px] text-slate1 leading-relaxed">
                Il cliente dovrà indicare nome ed email e accettare le Condizioni di Utilizzo prima di scaricare.
                L&apos;accettazione viene firmata digitalmente e registrata nel Registro Audit Download.
              </p>
              {created && (
                <div className="border border-emerald-300 bg-emerald-50 rounded-sm p-3 space-y-2" data-testid="asset-share-created-box">
                  <div className="flex items-center gap-2 text-emerald-700 text-xs font-medium">
                    <CheckCircle2 size={14} /> Link generato — scade il {fmtDate(created.expires_at)}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={created.share_url}
                      onFocus={(e) => e.target.select()}
                      className="flex-1 px-2 py-1.5 text-xs bg-white border border-emerald-300 rounded-sm font-mono text-ink"
                      data-testid="asset-share-created-url"
                    />
                    <button
                      onClick={() => copyText(created.share_url)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-[10px] tracking-[0.15em] uppercase rounded-sm hover:bg-emerald-700 transition-colors"
                      data-testid="asset-share-copy-btn"
                    >
                      <Copy size={11} /> Copia
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Lista link esistenti */}
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 mb-2">
              Link esistenti {loading ? "…" : `(${links.length})`}
            </div>
            {links.length === 0 && !loading ? (
              <div className="bg-white border border-ink/10 rounded-sm p-4 text-sm text-slate1" data-testid="asset-share-empty">
                Nessun link condiviso finora.
              </div>
            ) : (
              <div className="space-y-2">
                {links.map((l) => {
                  const active = !l.expired && l.is_active;
                  const hrs = hoursLeft(l.expires_at);
                  return (
                    <div key={l.id} className="bg-white border border-ink/10 rounded-sm p-3 flex items-center gap-3 flex-wrap" data-testid={`asset-share-link-${l.id}`}>
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-ink font-medium truncate max-w-[280px]" title={l.name}>{l.name}</span>
                          {active ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-emerald-50 border border-emerald-300 text-emerald-700 text-[9px] tracking-[0.15em] uppercase">
                              <Clock size={9} /> Attivo · {hrs}h
                            </span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded-sm bg-rose-50 border border-rose-300 text-rose-700 text-[9px] tracking-[0.15em] uppercase">Scaduto</span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate1 mt-0.5 flex items-center gap-3 flex-wrap">
                          <span>{(l.asset_ids || []).length} file</span>
                          <span>Creato {fmtDate(l.created_at)}</span>
                          <span className="inline-flex items-center gap-1"><Eye size={10} /> {l.views_count || 0}</span>
                          <span className="inline-flex items-center gap-1"><CheckCircle2 size={10} /> {l.accepts_count || 0} accettazioni</span>
                          <span className="inline-flex items-center gap-1"><Download size={10} /> {l.downloads_count || 0}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 ml-auto">
                        <button
                          onClick={() => copyText(l.share_url)}
                          disabled={!active}
                          className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5 disabled:opacity-30 transition-colors"
                          title="Copia link"
                          data-testid={`asset-share-copy-${l.id}`}
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          onClick={() => deleteLink(l.id)}
                          className="p-1.5 text-rose-600 hover:text-rose-700 rounded-sm hover:bg-rose-50 transition-colors"
                          title="Revoca link"
                          data-testid={`asset-share-delete-${l.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
